import {
  createContext,
  useContext,
  useId,
  type ReactNode,
  type HTMLAttributes,
} from 'react';
import clsx from 'clsx';

import { ErrorIcon } from '../internal/icons';
import { joinIds } from '../internal/aria';
import { VisuallyHidden } from './VisuallyHidden';
import styles from './FormField.module.css';

/** The attribute bag a control must spread to be correctly wired. */
export interface FieldControlAria {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  required: boolean | undefined;
}

const FieldContext = createContext<FieldControlAria | null>(null);

/**
 * Read the wiring supplied by an enclosing `FormField`. Returns null when the
 * control is used standalone, so every control works both ways.
 */
export const useFieldAria = (): FieldControlAria | null => useContext(FieldContext);

/**
 * Merge a control's own props over the field wiring. Explicit props win — a
 * caller that passes its own id or describedby means it.
 */
export const useMergedFieldAria = (explicit: {
  id?: string;
  describedBy?: string | undefined;
  invalid?: boolean;
  required?: boolean;
}): FieldControlAria => {
  const field = useFieldAria();
  const fallbackId = useId();
  return {
    id: explicit.id ?? field?.id ?? fallbackId,
    'aria-describedby': joinIds(field?.['aria-describedby'], explicit.describedBy),
    'aria-invalid': explicit.invalid === true || field?.['aria-invalid'] === true ? true : undefined,
    required: explicit.required ?? field?.required,
  };
};

export interface FormFieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  /** Persistent guidance. Wired through aria-describedby, never a placeholder. */
  hint?: ReactNode;
  /**
   * Validation message. Its presence is what sets aria-invalid — an invalid
   * control with no message is a dead end for a screen-reader user.
   */
  error?: ReactNode;
  required?: boolean;
  /** Keeps the label in the accessibility tree while removing it visually. */
  hideLabel?: boolean;
  /** Explicit id for the control, when a caller needs a stable one. */
  id?: string;
  /**
   * The control. Either a node (wired through context) or a render function for
   * a control that cannot read context — a third-party editor, a canvas.
   */
  children: ReactNode | ((aria: FieldControlAria) => ReactNode);
}

/**
 * Label / hint / error wiring in one place. This is the component that makes
 * `aria-describedby` and `aria-invalid` impossible to forget.
 */
export function FormField({
  label,
  hint,
  error,
  required = false,
  hideLabel = false,
  id,
  className,
  children,
  ...rest
}: FormFieldProps) {
  const generated = useId();
  const controlId = id ?? `${generated}-control`;
  const hintId = `${generated}-hint`;
  const errorId = `${generated}-error`;
  const invalid = error !== undefined && error !== null && error !== false;

  const aria: FieldControlAria = {
    id: controlId,
    'aria-describedby': joinIds(hint !== undefined && hintId, invalid && errorId),
    'aria-invalid': invalid ? true : undefined,
    required: required ? true : undefined,
  };

  return (
    <div {...rest} className={clsx(styles.field, className)}>
      <label className={clsx(styles.label, hideLabel && styles.hiddenLabel)} htmlFor={controlId}>
        {label}
        {required && (
          <>
            <span className={styles.requiredMark} aria-hidden="true">
              *
            </span>
            <VisuallyHidden>(required)</VisuallyHidden>
          </>
        )}
      </label>

      <FieldContext.Provider value={aria}>
        <div className={styles.control}>
          {typeof children === 'function' ? children(aria) : children}
        </div>
      </FieldContext.Provider>

      {hint !== undefined && (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}

      {invalid && (
        <p className={styles.error} id={errorId}>
          <ErrorIcon className={styles.errorIcon} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
